import { Entypo } from "@expo/vector-icons";
import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import classNames from "classnames";
import Button from "components/Button";
import Dialog from "components/Dialog";
import Input from "components/Input";
import Spacer from "components/Spacer";
import {
  setSelectedBillingInterval,
  setSelectedPlanPackage,
} from "features/slices/buyFlowSlice";
import {
  setQuantity,
  setSelectedCmsPlan,
  setSignupCharges,
  setStep,
} from "features/slices/shopPagePhaseTwoSlice";
import { BillingInterval } from "features/types";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import useAuth from "hooks/useAuth";
import useCurrentUser from "hooks/useCurrentUser";
import { getSanityImage } from "lib/sanity";
import { CmsPlan, ShopPagePhaseTwo } from "lib/sanity.types";
import isEmpty from "lodash/isEmpty";
import { fetchSignupChargeRequest } from "queries/account.queries";
import { trackConversion } from "queries/conversion.queries";
import { useFetchPlanPackagesQuery } from "queries/planPackage.queries";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ImageBackground, Pressable, StyleSheet, View } from "react-native";
import PortableText from "screens/Shop/components/PortableText";
import { formatCurrency } from "utils/appUtils";
import { toAddNewPlan, toGiftPage } from "utils/navigationUtils";
import { getPlanType } from "utils/planPackageUtils";
import OrderSummary from "./components/OrderSummary";
import { getRecurringPrice, getSinglePrice, planIdProperty } from "./helpers";

const StepTwo = ({
  shopPage,
  scrollToNext,
}: {
  shopPage: ShopPagePhaseTwo;
  scrollToNext: () => void;
}) => {
  const { group, selectedCmsPlan, signupCharges, quantity } = useAppSelector(
    (state) => state.shopPagePhaseTwo
  );
  const { selectedBillingInterval } = useAppSelector((state) => state.buyFlow);
  const dispatch = useAppDispatch();

  const auth = useAuth();

  const [redirectDialogVisible, setRedirectDialogVisible] = useState(false);

  const { currentMember } = useCurrentUser();
  const { data: branding } = useFetchTenantBrandingQuery();
  const { data: planPackages } = useFetchPlanPackagesQuery();
  const { mutateAsync: fetchSignupCharge } = useMutation(
    fetchSignupChargeRequest
  );
  const { mutateAsync: track } = useMutation(trackConversion);

  const viewContainer = useRef<HTMLDivElement | null>(null);
  const Cmsplans = shopPage.plans.filter((plan) => plan.group === group);
  const CmsplanIds = Cmsplans.map((plan) => plan[planIdProperty]);

  const billingIntervals = [
    { label: "Payment Plan", value: "MONTHLY" },
    { label: "Pay In Full", value: "FULL_SEASON" },
  ];

  const actualPlanPackages = planPackages?.filter((planPackage) =>
    CmsplanIds.includes(planPackage.id)
  );

  const getPlanPrice = (plan: CmsPlan) => {
    const planId = plan[planIdProperty];
    const signupChargeDetails = signupCharges.find(
      (charge) =>
        charge.planPackageId === planId &&
        charge.billingInterval === selectedBillingInterval
    );
    if (!signupChargeDetails) return "N/A";
    const { signupCharge } = signupChargeDetails;
    return formatCurrency(
      selectedBillingInterval === "MONTHLY"
        ? getRecurringPrice(signupCharge)
        : getSinglePrice(signupCharge)
    );
  };

  const getPlanPriceSuffix = (plan: CmsPlan) => {
    return selectedBillingInterval === "MONTHLY"
      ? plan.monthlyPriceSuffix
      : plan.payInFullPriceSuffix;
  };

  const getSeatMapUrl = useMemo(() => {
    if (!selectedCmsPlan) return undefined;

    const seatMapName = selectedCmsPlan.seatMapName;
    const assetRef = shopPage.seatMaps.find(
      (seatMap) => seatMap.name === seatMapName
    )?.image.asset._ref;

    if (!assetRef) return undefined;

    return getSanityImage(assetRef);
  }, [selectedCmsPlan]);

  const fetchSignupCharges = async () => {
    const result = [];
    for (const planPackage of actualPlanPackages!) {
      for (const billingInterval of planPackage.billingIntervals) {
        const signupCharge = await fetchSignupCharge({
          planPackageIds: [planPackage.id],
          billingInterval,
        });
        result.push({
          planPackageId: planPackage.id,
          billingInterval,
          signupCharge,
        });
      }
    }
    dispatch(setSignupCharges(result));
  };

  const isVoucherPlan =
    !isEmpty(actualPlanPackages) &&
    getPlanType(actualPlanPackages![0]) === "VOUCHER";

  const shouldShowBillingInterval = useMemo(() => {
    if (isEmpty(actualPlanPackages)) return false;
    return actualPlanPackages![0].billingIntervals.length > 1;
  }, [planPackages]);

  const handleContinueBtnPress = async () => {
    if (auth.isLoggedIn) {
      setRedirectDialogVisible(true);
    } else {
      dispatch(setStep(3));
      scrollToNext();
      await track({
        step: "PROCEED_TO_USER_INFO",
        flow: "SHOP_PHASE_TWO",
      });
    }
  };

  const redirectToAddPlanFlow = () => {
    toAddNewPlan(String(currentMember!.id));
    setRedirectDialogVisible(false);
  };

  const setActualPlanPackageInBuyFlowSlice = (plan: CmsPlan) => {
    const actualPlanPackage = actualPlanPackages?.find(
      (p) => p.id === plan[planIdProperty]
    );
    if (actualPlanPackage !== undefined) {
      dispatch(setSelectedPlanPackage(actualPlanPackage));
    }
  };
  useEffect(() => {
    if (group === "") return;
    dispatch(setSelectedCmsPlan(Cmsplans[0]));
  }, [group]);

  useEffect(() => {
    if (isEmpty(actualPlanPackages)) return;
    // Initialize billing interval
    // Some plans only have one billing interval, and some have multiple
    dispatch(
      setSelectedBillingInterval(actualPlanPackages![0].billingIntervals[0])
    );

    fetchSignupCharges();
    setActualPlanPackageInBuyFlowSlice(Cmsplans[0]);
  }, [planPackages]);

  return (
    <div ref={(ref) => (viewContainer.current = ref)}>
      <Text category="h6">Select Your Plan</Text>
      <View className="flex-col justify-center">
        {shouldShowBillingInterval && (
          <View
            className="mt-4 flex-row justify-center items-center border rounded-lg overflow-hidden text-center text-white"
            style={{ backgroundColor: branding?.primaryColor }}
          >
            {billingIntervals.map((interval) => {
              const selected = interval.value === selectedBillingInterval;
              return (
                <Pressable
                  key={interval.value}
                  className={classNames(
                    "p-1 border rounded-md border-transparent grow text-white"
                  )}
                  onPress={() =>
                    dispatch(
                      setSelectedBillingInterval(
                        interval.value as BillingInterval
                      )
                    )
                  }
                >
                  <div
                    className={classNames("rounded-md p-2", {
                      "text-black": selected,
                      "bg-white": selected,
                    })}
                  >
                    {interval.label}
                  </div>
                </Pressable>
              );
            })}
          </View>
        )}
        <View className="mt-4 grid gap-4 relative">
          <View>
            {Cmsplans.map((plan) => {
              const isPlanSelected = selectedCmsPlan?._key === plan._key;

              return (
                <View key={plan._key} className="mb-4">
                  <View
                    className={classNames("p-2 rounded-xl mb-2", {
                      "border-[4px]": isPlanSelected,
                    })}
                    style={{
                      borderColor: branding?.primaryColor,
                    }}
                  >
                    <ImageBackground
                      source={{
                        uri: `https://branding.fanrally.com/logos/${branding?.name}_dark.svg`,
                      }}
                      resizeMode="contain"
                      imageStyle={{
                        opacity: 0.3,
                        left: "50%",
                        transform: [{ rotate: "45deg" }, { scale: 1.5 }],
                      }}
                      style={{
                        backgroundColor: branding?.primaryColor,
                        overflow: "hidden",
                      }}
                    >
                      <Pressable
                        className="w-full p-8 md:min-w-[400px] border-[8px] rounded-md"
                        onPress={() => {
                          dispatch(setSelectedCmsPlan(plan));
                          // Need this for NewMemberForm, to find the validation rules for this plan
                          setActualPlanPackageInBuyFlowSlice(plan);
                        }}
                        style={{
                          borderColor: branding?.primaryColor,
                        }}
                      >
                        <View style={[styles.triangle, styles.triangleLeft]} />
                        <View style={[styles.triangle, styles.triangleRight]} />
                        <View>
                          <div className="text-white text-3xl font-semibold">
                            {plan.name}
                          </div>
                          {isPlanSelected && (
                            <div style={{ color: branding?.secondaryColor }}>
                              <PortableText value={plan.description} />
                            </div>
                          )}
                          <Spacer />
                          <div
                            className="self-end space-x-1"
                            style={{
                              color: plan.priceColor
                                ? plan.priceColor.hex
                                : branding?.secondaryColor,
                            }}
                          >
                            <span className="text-xl md:text-3xl font-bold">
                              {getPlanPrice(plan)}
                            </span>
                            <span className="text-xs md:text-sm">
                              {getPlanPriceSuffix(plan)}
                            </span>
                          </div>
                          <Text
                            category="h5"
                            status="control"
                            style={{
                              textAlign: "right",
                              letterSpacing: 1.5,
                            }}
                          ></Text>
                        </View>
                      </Pressable>
                    </ImageBackground>
                  </View>
                  {isVoucherPlan && selectedCmsPlan?._key === plan._key && (
                    <View className="mb-2 flex-row justify-center items-center gap-4">
                      <Pressable
                        onPress={() =>
                          dispatch(setQuantity(Math.max(quantity - 1, 1)))
                        }
                      >
                        <Entypo name="minus" size={24} color="black" />
                      </Pressable>
                      <Input style={{ width: 50 }} value={String(quantity)} />
                      <Pressable
                        testID="quantity-plus-button"
                        onPress={() => dispatch(setQuantity(quantity + 1))}
                      >
                        <Entypo name="plus" size={24} color="black" />
                      </Pressable>
                    </View>
                  )}
                  {isPlanSelected && (
                    <View className="md:hidden">
                      <Spacer>
                        <img src={getSeatMapUrl} />
                        <Button
                          onPress={handleContinueBtnPress}
                          style={{
                            marginTop: 24,
                            marginBottom: 36,
                            width: 300,
                            alignSelf: "center",
                          }}
                        >
                          CONTINUE
                        </Button>
                        {selectedCmsPlan.giftingEnabled && (
                          <a
                            className="text-center cursor-pointer text-xl font-semibold"
                            onClick={() =>
                              toGiftPage(selectedCmsPlan[planIdProperty])
                            }
                          >
                            Sending as a gift?
                          </a>
                        )}
                      </Spacer>
                      <OrderSummary />
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        </View>
      </View>
      <Dialog
        visible={redirectDialogVisible}
        setVisible={setRedirectDialogVisible}
        cancelText="Close"
        okText="Continue"
        onOk={redirectToAddPlanFlow}
      >
        <Text category="h6">Proceed to Add New Plan</Text>
        <Spacer />
        <span>
          You are currently logged in as:{" "}
          <span className="font-semibold">{auth.username!}</span>, To proceed,
          you'll be redirected to the in-app purchase flow. Please select 'Add
          New Plan' in the next screen.
        </span>
      </Dialog>
    </div>
  );
};

const TRIANGLE_SIZE = 25;
const styles = StyleSheet.create({
  triangle: {
    width: 0,
    height: 0,
    backgroundColor: "transparent",
    borderStyle: "solid",
    borderLeftWidth: TRIANGLE_SIZE,
    borderRightWidth: TRIANGLE_SIZE,
    borderBottomWidth: 20,
    borderLeftColor: "transparent",
    borderRightColor: "transparent",
    borderBottomColor: "#f5f5f5",
    position: "absolute",
  },
  triangleLeft: {
    transform: [{ rotate: "90deg" }],
    top: "50%",
    left: -TRIANGLE_SIZE,
  },
  triangleRight: {
    transform: [{ rotate: "-90deg" }],
    top: "50%",
    right: -TRIANGLE_SIZE,
  },
});
export default StepTwo;
